<h2 mat-dialog-title>
    {{ operationType === 'export' ? 'Export' : 'Import' }} Progress
</h2>

<mat-dialog-content>

    <!-- No operation found -->
    <div *ngIf="!operation" class="text-center p-4">
        <mat-spinner diameter="40" class="mx-auto mb-3"></mat-spinner>
        <p>Loading operation status...</p>
    </div>

    <!-- Queued -->
    <div *ngIf="operation?.status === 'queued'" class="text-center p-4">
        <mat-spinner diameter="40" class="mx-auto mb-3"></mat-spinner>
        <p>Waiting to start...</p>
        <p class="text-muted">The operation is queued and will begin shortly.</p>
    </div>

    <!-- Processing -->
    <div *ngIf="operation?.status === 'processing'" class="p-2">
        <!-- Stall warning: worker hasn't updated progress in a while. -->
        <div *ngIf="stalled" class="alert alert-warning mb-3">
            <mat-icon class="align-middle mr-2">warning</mat-icon>
            This operation hasn't reported progress in a while. The queue worker may be stalled or busy with a large entity. Progress will resume if it's still running.
        </div>

        <div class="mb-3">
            <strong>Phase {{ operation.progress_data?.current_phase || '-' }}</strong>
            <span *ngIf="operation.progress_data?.current_entity" class="ml-2 text-muted">
                Processing: {{ operation.progress_data.current_entity }}
            </span>
        </div>

        <mat-progress-bar
            mode="determinate"
            [value]="operation.progress_data?.percentage || 0"
            class="mb-2">
        </mat-progress-bar>

        <div class="d-flex justify-content-between mb-3">
            <span>{{ operation.progress_data?.records_processed || 0 }} records processed</span>
            <span>{{ (operation.progress_data?.percentage || 0) | number:'1.0-1' }}%</span>
        </div>

        <!-- Warnings (expandable) -->
        <div *ngIf="operation.progress_data?.warnings?.length" class="mb-2">
            <a class="text-warning" style="cursor: pointer;" (click)="showWarnings = !showWarnings">
                <mat-icon style="font-size: 14px; vertical-align: middle;">warning</mat-icon>
                {{ operation.progress_data.warnings.length }} warning(s)
                <mat-icon style="font-size: 14px; vertical-align: middle;">
                    {{ showWarnings ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </a>
            <div *ngIf="showWarnings" class="log-panel mt-1 p-2 border rounded bg-light" style="max-height: 150px; overflow-y: auto; font-size: 12px;">
                <div *ngFor="let warning of operation.progress_data.warnings" class="text-warning mb-1">
                    {{ warning }}
                </div>
            </div>
        </div>

        <!-- Errors (expandable) -->
        <div *ngIf="operation.progress_data?.errors?.length" class="mb-2">
            <a class="text-danger" style="cursor: pointer;" (click)="showErrors = !showErrors">
                <mat-icon style="font-size: 14px; vertical-align: middle;">error</mat-icon>
                {{ operation.progress_data.errors.length }} error(s)
                <mat-icon style="font-size: 14px; vertical-align: middle;">
                    {{ showErrors ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </a>
            <div *ngIf="showErrors" class="log-panel mt-1 p-2 border rounded bg-light" style="max-height: 150px; overflow-y: auto; font-size: 12px;">
                <div *ngFor="let error of operation.progress_data.errors" class="text-danger mb-1">
                    {{ error }}
                </div>
            </div>
        </div>
    </div>

    <!-- Completed (Export) -->
    <div *ngIf="operation?.status === 'completed' && operation?.type === 'export'" class="text-center p-4">
        <mat-icon class="text-success mb-2" style="font-size: 48px; width: 48px; height: 48px;">check_circle</mat-icon>
        <h3>Export Complete</h3>
        <p class="text-muted">The account data has been exported successfully.</p>
        <button mat-raised-button color="primary" (click)="onDownload()">
            <mat-icon>download</mat-icon> Download Export
        </button>
    </div>

    <!-- Completed (Import) -->
    <div *ngIf="operation?.status === 'completed' && operation?.type === 'import'" class="p-4">
        <div class="text-center">
            <mat-icon class="text-success mb-2" style="font-size: 48px; width: 48px; height: 48px;">check_circle</mat-icon>
            <h3>Import Complete</h3>
        </div>

        <table class="table table-sm mt-3" *ngIf="operation.result_data">
            <tbody>
                <tr>
                    <td class="text-right"><strong>Records Imported:</strong></td>
                    <td>{{ operation.result_data.total_records || 0 }}</td>
                </tr>
                <tr>
                    <td class="text-right"><strong>Files Transferred:</strong></td>
                    <td>{{ operation.result_data.total_files || 0 }}</td>
                </tr>
                <tr>
                    <td class="text-right"><strong>Warnings:</strong></td>
                    <td>
                        <span [class.text-warning]="operation.result_data.warnings > 0">
                            {{ operation.result_data.warnings || 0 }}
                        </span>
                    </td>
                </tr>
                <tr>
                    <td class="text-right"><strong>Errors:</strong></td>
                    <td>
                        <span [class.text-danger]="operation.result_data.errors > 0">
                            {{ operation.result_data.errors || 0 }}
                        </span>
                    </td>
                </tr>
                <tr>
                    <td class="text-right"><strong>Elapsed Time:</strong></td>
                    <td>{{ getElapsedTime() }}</td>
                </tr>
            </tbody>
        </table>

        <!-- Warning details from progress_data (last 50 captured during import) -->
        <div *ngIf="operation.progress_data?.warnings?.length" class="mt-3">
            <a class="text-warning" style="cursor: pointer;" (click)="showWarnings = !showWarnings">
                <mat-icon style="font-size: 16px; vertical-align: middle;">warning</mat-icon>
                View {{ operation.progress_data.warnings.length }} warning(s)
                <mat-icon style="font-size: 16px; vertical-align: middle;">
                    {{ showWarnings ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </a>
            <div *ngIf="showWarnings" class="log-panel mt-2 p-2 border rounded bg-light" style="max-height: 200px; overflow-y: auto; font-size: 12px;">
                <div *ngFor="let warning of operation.progress_data.warnings; let i = index" class="text-warning mb-1">
                    <small class="text-muted mr-1">{{ i + 1 }}.</small> {{ warning }}
                </div>
                <div *ngIf="operation.result_data.warnings > operation.progress_data.warnings.length" class="text-muted mt-1">
                    <small>Showing last {{ operation.progress_data.warnings.length }} of {{ operation.result_data.warnings }} total warnings.</small>
                </div>
            </div>
        </div>

        <!-- Error details from progress_data -->
        <div *ngIf="operation.progress_data?.errors?.length" class="mt-2">
            <a class="text-danger" style="cursor: pointer;" (click)="showErrors = !showErrors">
                <mat-icon style="font-size: 16px; vertical-align: middle;">error</mat-icon>
                View {{ operation.progress_data.errors.length }} error(s)
                <mat-icon style="font-size: 16px; vertical-align: middle;">
                    {{ showErrors ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </a>
            <div *ngIf="showErrors" class="log-panel mt-2 p-2 border rounded bg-light" style="max-height: 200px; overflow-y: auto; font-size: 12px;">
                <div *ngFor="let error of operation.progress_data.errors; let i = index" class="text-danger mb-1">
                    <small class="text-muted mr-1">{{ i + 1 }}.</small> {{ error }}
                </div>
                <div *ngIf="operation.result_data.errors > operation.progress_data.errors.length" class="text-muted mt-1">
                    <small>Showing last {{ operation.progress_data.errors.length }} of {{ operation.result_data.errors }} total errors.</small>
                </div>
            </div>
        </div>
    </div>

    <!-- Failed -->
    <div *ngIf="operation?.status === 'failed'" class="p-4">
        <div class="text-center">
            <mat-icon class="text-danger mb-2" style="font-size: 48px; width: 48px; height: 48px;">error</mat-icon>
            <h3>Operation Failed</h3>
        </div>
        <div class="alert alert-danger mt-3" *ngIf="operation.error_message">
            <strong>Error:</strong> {{ operation.error_message }}
        </div>
        <p class="text-muted text-center" *ngIf="!operation.error_message">An unexpected error occurred during the operation.</p>

        <!-- Show any warnings/errors captured before failure -->
        <div *ngIf="operation.progress_data?.warnings?.length" class="mt-3">
            <a class="text-warning" style="cursor: pointer;" (click)="showWarnings = !showWarnings">
                <mat-icon style="font-size: 16px; vertical-align: middle;">warning</mat-icon>
                {{ operation.progress_data.warnings.length }} warning(s) before failure
                <mat-icon style="font-size: 16px; vertical-align: middle;">
                    {{ showWarnings ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </a>
            <div *ngIf="showWarnings" class="log-panel mt-2 p-2 border rounded bg-light" style="max-height: 200px; overflow-y: auto; font-size: 12px;">
                <div *ngFor="let warning of operation.progress_data.warnings; let i = index" class="text-warning mb-1">
                    <small class="text-muted mr-1">{{ i + 1 }}.</small> {{ warning }}
                </div>
            </div>
        </div>

        <div *ngIf="operation.progress_data?.errors?.length" class="mt-2">
            <a class="text-danger" style="cursor: pointer;" (click)="showErrors = !showErrors">
                <mat-icon style="font-size: 16px; vertical-align: middle;">error</mat-icon>
                {{ operation.progress_data.errors.length }} error(s) before failure
                <mat-icon style="font-size: 16px; vertical-align: middle;">
                    {{ showErrors ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </a>
            <div *ngIf="showErrors" class="log-panel mt-2 p-2 border rounded bg-light" style="max-height: 200px; overflow-y: auto; font-size: 12px;">
                <div *ngFor="let error of operation.progress_data.errors; let i = index" class="text-danger mb-1">
                    <small class="text-muted mr-1">{{ i + 1 }}.</small> {{ error }}
                </div>
            </div>
        </div>
    </div>

</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button (click)="onClose()">Close</button>
</mat-dialog-actions>
